import { useEffect, useState, useCallback } from 'react';
import { useQuery, useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRouteParams } from '@/contexts/RouteContext';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { Loader2, Play, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import gql from 'graphql-tag';

import {
	REPORT_DEFINITION_QUERY,
	type ReportDefinitionQueryResponse
} from './reports.gql';
import { ReportFiltersBar } from './report-filters-bar';
import { ReportDataTable } from './report-data-table';
import {
	flattenReportData,
	buildGraphQlVariables,
	resolveFilterDefaults,
	validateFilters,
	type FilterValidationErrors
} from './report-utils';

export function ReportViewer() {
	const params = useRouteParams();
	const reportKey = params.reportKey;
	const { t } = useTranslation('reports');
	const navigate = useNavigate();
	const client = useApolloClient();
	const { setPageHeader, resetPageHeader } = usePageHeader();

	// Fetch the report definition
	const { data: defData, loading: defLoading, error: defError } =
		useQuery<ReportDefinitionQueryResponse>(REPORT_DEFINITION_QUERY, {
			variables: { reportKey: reportKey ?? '' },
			skip: !reportKey,
			fetchPolicy: 'cache-and-network'
		});

	const definition = defData?.reportDefinition ?? null;

	// Page header
	useEffect(() => {
		setPageHeader({
			title: definition?.title ?? t('viewer.loading'),
			description: definition?.description ?? '',
			showBackButton: true,
			onBackClick: () => navigate('/reports')
		});
		return () => {
			resetPageHeader();
		};
	}, [setPageHeader, resetPageHeader, navigate, definition, t]);

	// Filter state — initialize from defaultValues
	const [filterValues, setFilterValues] = useState<Record<string, unknown>>(
		{}
	);

	// Search within results (lifted from data table for header placement)
	const [searchText, setSearchText] = useState('');

	// Handler for individual filter value changes
	// Also resets dependent (cascading) filters when a parent filter changes
	const handleFilterChange = useCallback((key: string, value: unknown) => {
		setFilterValues(prev => {
			const next = { ...prev, [key]: value };
			// Reset any filters that depend on this filter (cascading)
			if (definition) {
				for (const f of definition.filters) {
					if (f.dependsOnFilterKey === key) {
						next[f.key] = undefined;
					}
				}
			}
			return next;
		});
	}, [definition]);

	// Initialize filter defaults once definition loads
	useEffect(() => {
		if (!definition) return;
		setFilterValues(resolveFilterDefaults(definition.filters));
	}, [definition]);

	// Filter validation errors
	const [filterErrors, setFilterErrors] = useState<FilterValidationErrors>({});

	// Clear validation error for a filter when its value changes
	const handleFilterChangeWithClear = useCallback((key: string, value: unknown) => {
		handleFilterChange(key, value);
		setFilterErrors(prev => {
			if (!prev[key]) return prev;
			const next = { ...prev };
			delete next[key];
			return next;
		});
	}, [handleFilterChange]);

	// Report data state
	const [rows, setRows] = useState<Record<string, unknown>[]>([]);
	const [reportLoading, setReportLoading] = useState(false);
	const [reportError, setReportError] = useState<string | null>(null);
	const [hasRun, setHasRun] = useState(false);

	// Execute the report GraphQL query
	const runReport = useCallback(async () => {
		if (!definition?.graphQlQuery) return;

		// Validate filters before executing
		const errors = validateFilters(definition.filters, filterValues);
		setFilterErrors(errors);
		if (Object.keys(errors).length > 0) return;

		setReportLoading(true);
		setReportError(null);
		setHasRun(true);

		try {
			// Parse the GraphQL query string from the definition
			const queryDoc = gql`
				${definition.graphQlQuery}
			`;

			// Build variables from filters
			const variables = buildGraphQlVariables(definition, filterValues);

			const { data, errors } = await client.query({
				query: queryDoc,
				variables,
				fetchPolicy: 'network-only'
			});

			if (errors?.length) {
				setReportError(errors.map(e => e.message).join(', '));
				setRows([]);
			} else {
				// Flatten the response using resultArrayPath + column field paths
				const flatRows = flattenReportData(data, definition);
				setRows(flatRows);
			}
		} catch (err: any) {
			console.error('Report execution error:', err);
			setReportError(err?.message || 'An error occurred while running the report');
			setRows([]);
		} finally {
			setReportLoading(false);
		}
	}, [definition, filterValues, client]);

	// Loading definition
	if (defLoading && !defData) {
		return (
			<div className="flex h-64 items-center justify-center">
				<Loader2 className="h-8 w-8 animate-spin text-primary" />
			</div>
		);
	}

	// Error loading definition
	if (defError || !definition) {
		return (
			<div className="mx-auto max-w-5xl px-6 py-8">
				<div className="flex h-48 items-center justify-center">
					<div className="text-center">
						<p className="text-lg font-medium text-destructive">
							{defError
								? t('viewer.errorLoading')
								: t('viewer.notFound')}
						</p>
						{defError && (
							<p className="mt-1 text-sm text-muted-foreground">
								{defError.message}
							</p>
						)}
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="mx-auto max-w-[1400px] px-6 py-6 h-full flex flex-col overflow-hidden">
			{/* Configuration card: filters + search */}
			<div className="rounded-xl border border-border bg-card shadow-sm mb-6">
				{/* Filters + search */}
				{definition.filters.length > 0 && (
					<div className="bg-muted/30 px-5 py-4 rounded-xl">
						<ReportFiltersBar
							filters={definition.filters}
							values={filterValues}
							onChange={handleFilterChangeWithClear}
							onRun={runReport}
							isRunning={reportLoading}
							errors={filterErrors}
							searchText={searchText}
							onSearchChange={setSearchText}
							searchPlaceholder={t('viewer.searchPlaceholder')}
						/>
					</div>
				)}

				{/* Run button + search when no filters */}
				{definition.filters.length === 0 && (
					<div className="bg-muted/30 px-5 py-4 rounded-xl flex items-end justify-between gap-4">
						<Button
							onClick={runReport}
							disabled={reportLoading}
							className="gap-2"
						>
							{reportLoading ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<Play className="h-4 w-4" />
							)}
							{t('viewer.run')}
						</Button>
						<div className="relative">
							<Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
							<Input
								id="report-search"
								name="report-search"
								value={searchText}
								onChange={e => setSearchText(e.target.value)}
								placeholder={t('viewer.searchPlaceholder')}
								className="h-8 w-52 pl-8 text-sm"
							/>
						</div>
					</div>
				)}
			</div>

			{/* Error */}
			{reportError && (
				<div className="mb-6 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{reportError}
				</div>
			)}

			{/* Results */}
			{hasRun && !reportError && (
				<ReportDataTable
					rows={rows}
					definition={definition}
					isLoading={reportLoading}
					searchText={searchText}
					className="flex-1 min-h-0"
				/>
			)}

			{/* Empty state before first run */}
			{!hasRun && !reportLoading && (
				<div className="flex h-48 items-center justify-center rounded-lg border border-dashed border-border">
					<div className="text-center">
						<button
							type="button"
							onClick={runReport}
							className="mx-auto flex h-14 w-14 items-center justify-center rounded-full transition-colors hover:bg-accent cursor-pointer"
						>
							<Play className="h-10 w-10 text-muted-foreground/50" />
						</button>
						<p className="mt-3 text-sm text-muted-foreground">
							{t('viewer.readyToRun')}
						</p>
					</div>
				</div>
			)}
		</div>
	);
}
